import { getApplicationSettings } from "owa-application-settings";
import type { MailboxInfo } from "owa-client-types";
import type { SearchOptionalDiagnosticData } from "owa-copilot-feedback";
import { isAccountFeatureEnabled } from "owa-feature-flags";
import { getEntityRewriteDiagnostics } from "owa-search-query-parser/lib/getEntityRewriteDiagnostics";
import { getScenarioStore } from "owa-search-store";
import type SearchScenarioId from "owa-search-store/lib/store/schema/SearchScenarioId";
import getIsSearchingWithinFromFolder from "../selectors/getIsSearchingWithinFromFolder";

/**
 * Builds the entity-recognition diagnostics attached to a `Search` scenario
 * thumbs up/down, so feedback submitted through the shared Copilot feedback flow
 * carries the literal query, the matched person alias(es), and the reason codes
 * that explain the rewrite.
 *
 * This payload only leaves the device when the user submits feedback and the
 * tenant's diagnostics-sharing policy allows it — it is never logged to telemetry.
 */
export default function getEntityRecognitionSearchDiagnostics(mailboxInfo: MailboxInfo, searchScenarioId: SearchScenarioId): SearchOptionalDiagnosticData {
    const searchStore = getScenarioStore(searchScenarioId);
    const query = searchStore.searchText;
    
    const base = {
        client: getApplicationSettings("PlatformType").isMonarch ? "Monarch" : "OWA",
        flightEnabled: isAccountFeatureEnabled("sea-entityRecognition", mailboxInfo),
        searchSessionGuid: searchStore.searchSessionGuid,
        searchQueryId: searchStore.currentSearchQueryId,
        query,
    } as const;
    
    // The rewrite only ran for the executed search under gates (supported tab, From-folder
    // scope, not a conversation-topic/recipient-expanded/altered query) that the matcher
    // itself does not enforce. Read the flag captured at execution time so feedback never
    // reports a rewrite for a query that was never rewritten, and so the matcher is not
    // re-run (ungated) at feedback time.
    if (!searchStore.entityRecognitionApplied) {
        return {
            ...base,
            rewriteApplied: false,
            skippedReason: "NotAppliedToExecutedQuery",
            entityCount: 0,
            queryTokenCount: 0,
            residualKeywordCount: 0,
            matchedEntities: [],
        };
    }
    
    const diagnostics = getEntityRewriteDiagnostics(query, mailboxInfo, getIsSearchingWithinFromFolder() ? ["From"] : ["To", "CC", "BCC"]);
    
    return {
        ...base,
        rewriteApplied: diagnostics?.rewriteApplied ?? false,
        skippedReason: diagnostics?.skippedReason,
        rewrittenQuery: diagnostics?.rewrittenQuery,
        entityCount: diagnostics?.entityCount ?? 0,
        queryTokenCount: diagnostics?.queryTokenCount ?? 0,
        residualKeywordCount: diagnostics?.residualKeywordCount ?? 0,
        topMatchLayer: diagnostics?.topMatchLayer,
        topMatchedTextLength: diagnostics?.topMatchedTextLength,
        topTargetNameLength: diagnostics?.topTargetNameLength,
        topScore: diagnostics?.topScore,
        topAffinity: diagnostics?.topAffinity,
        topRankingScore: diagnostics?.topRankingScore,
        matchedEntities: (diagnostics?.matchedEntities ?? []).map(entity => ({
            matchedText: entity.matchedText,
            displayName: entity.displayName,
            email: entity.email,
            layer: entity.layer,
            score: entity.score,
            affinity: entity.affinity,
            rankingScore: entity.rankingScore,
        })),
    };
}
